import { useEffect, useId, useRef, useState, type CSSProperties } from "react";

import {
  CALC_DEFAULTS,
  CALC_LIMITS,
  calculatePrice,
  feeFormula,
  formatPesos,
  manualWorkItems,
  priceSummary,
  toWholeNumber,
} from "./priceCalculation";
import "./PriceCalculator.css";

/** Calculadora de precio de /organizador (§8): jugadores que pagan, costo de
 * inscripción y primer torneo → cuánto cobras, la comisión de Torneos123 y
 * cuánto te queda. Estado local; las cuentas viven en priceCalculation.ts. */
export function PriceCalculator() {
  const baseId = useId();
  const ids = {
    title: `${baseId}-title`,
    players: `${baseId}-players`,
    playersLabel: `${baseId}-players-label`,
    cost: `${baseId}-cost`,
    first: `${baseId}-first`,
  };

  // Texto crudo de cada campo: deja borrar y reescribir sin que el número
  // salte; la cuenta usa el valor ya limpio y al salir del campo se corrige.
  const [playersRaw, setPlayersRaw] = useState<string>(String(CALC_DEFAULTS.players));
  const [costRaw, setCostRaw] = useState<string>(String(CALC_DEFAULTS.cost));
  const [firstTournament, setFirstTournament] = useState(false);

  const players = toWholeNumber(playersRaw, 0, CALC_LIMITS.playersMax);
  const cost = toWholeNumber(costRaw, 0, CALC_LIMITS.costMax);
  const result = calculatePrice({ players, cost, firstTournament });
  const work = manualWorkItems(result);

  // Aviso para lectores de pantalla: solo después de que la persona mueve
  // algo y cuando deja de moverlo (el deslizador cambia muchas veces).
  const touched = useRef(false);
  const summary = priceSummary(result);
  const [announcement, setAnnouncement] = useState("");
  useEffect(() => {
    if (!touched.current) return;
    const timer = window.setTimeout(() => setAnnouncement(summary), 700);
    return () => window.clearTimeout(timer);
  }, [summary]);

  const fill = `${(players / CALC_LIMITS.playersMax) * 100}%`;

  return (
    <div className="t123-calc" role="group" aria-labelledby={ids.title}>
      <div className="t123-calc__head">
        <h3 id={ids.title}>Haz la cuenta de tu torneo</h3>
        <p>Cambia los números y ve cuánto te queda.</p>
      </div>

      <div className="t123-calc__fields">
        <div className="t123-calc__field">
          <div className="t123-calc__row">
            <label id={ids.playersLabel} htmlFor={ids.players}>
              Jugadores que pagan
            </label>
            <input
              id={ids.players}
              className="t123-calc__input t123-calc__input--players"
              type="number"
              inputMode="numeric"
              min={0}
              max={CALC_LIMITS.playersMax}
              step={1}
              value={playersRaw}
              onChange={(event) => {
                touched.current = true;
                setPlayersRaw(event.target.value);
              }}
              onBlur={() => setPlayersRaw(String(players))}
            />
          </div>
          <input
            className="t123-calc__range"
            type="range"
            min={0}
            max={CALC_LIMITS.playersMax}
            step={1}
            value={players}
            aria-labelledby={ids.playersLabel}
            style={{ "--t123-calc-fill": fill } as CSSProperties}
            onChange={(event) => {
              touched.current = true;
              setPlayersRaw(event.target.value);
            }}
          />
          <div className="t123-calc__scale" aria-hidden="true">
            <span>0</span>
            <span>{CALC_LIMITS.playersMax}</span>
          </div>
        </div>

        <div className="t123-calc__field">
          <div className="t123-calc__row">
            <label htmlFor={ids.cost}>Costo de inscripción (MXN)</label>
            <div className="t123-calc__money">
              <span aria-hidden="true">$</span>
              <input
                id={ids.cost}
                className="t123-calc__input t123-calc__input--cost"
                type="number"
                inputMode="numeric"
                min={0}
                max={CALC_LIMITS.costMax}
                step={1}
                value={costRaw}
                onChange={(event) => {
                  touched.current = true;
                  setCostRaw(event.target.value);
                }}
                onBlur={() => setCostRaw(String(cost))}
              />
            </div>
          </div>
        </div>

        <label className={firstTournament ? "t123-calc__check is-checked" : "t123-calc__check"} htmlFor={ids.first}>
          <input
            id={ids.first}
            type="checkbox"
            checked={firstTournament}
            onChange={(event) => {
              touched.current = true;
              setFirstTournament(event.target.checked);
            }}
          />
          <span>Es mi primer torneo</span>
        </label>
      </div>

      <div className="t123-calc__results">
        <dl>
          <div className="t123-calc__line">
            <dt>Cobras en línea</dt>
            <dd>{formatPesos(result.collected)}</dd>
          </div>
          <div className="t123-calc__line">
            <dt>
              Comisión Torneos123 <span className="t123-calc__formula">{feeFormula(result)}</span>
            </dt>
            <dd>
              {result.promoApplied ? (
                <>
                  <span className="t123-sr-only">$0 por ser tu primer torneo; sin la promoción serían {formatPesos(result.feeWithoutPromo)}</span>
                  <span className="t123-calc__promo" aria-hidden="true">
                    <s>{formatPesos(result.feeWithoutPromo)}</s>
                    <span>$0 · primer torneo</span>
                  </span>
                </>
              ) : result.fee > 0 ? (
                `−${formatPesos(result.fee)}`
              ) : (
                formatPesos(0)
              )}
            </dd>
          </div>
          <div className="t123-calc__line t123-calc__line--net">
            <dt>Te queda antes de la comisión de Mercado Pago</dt>
            <dd>{formatPesos(result.net)}</dd>
          </div>
        </dl>
        {result.cost === 0 ? <p className="t123-calc__hint">Si tu torneo no cobra inscripción, no pagas comisión.</p> : null}
      </div>

      <div className="t123-calc__work">
        <h4>Trabajo que ya no haces a mano</h4>
        <ul>
          {work.map((item) => (
            <li key={item.key}>
              <span className="t123-calc__count">{item.count}</span>
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="t123-calc__fine">
        Cálculo de ejemplo con una inscripción por jugador. No incluye la comisión de Mercado Pago, que depende de tu cuenta.
      </p>

      <p className="t123-sr-only" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}
